import type { ReactNode } from "react";
import { scopeFor } from "@sts/schemas";
import type { Installation, InstallationsResponse } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { IconState } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { humanise, utcMoment } from "../lib/format.js";

/**
 * The installations board — jobs being worked on in the field.
 *
 * This screen exists because the photographs were reachable only by knowing
 * which job to open. An installer takes forty shots at a property and nobody in
 * the office can see that happen without first guessing where to look.
 *
 * WHAT IT REFUSES TO SAY. Every other product's version of this screen reports
 * how many photographs are "pending upload". This one cannot and does not,
 * because a phone that has not synced is a phone the server has not heard from,
 * and a count of what it is holding would be invented. What is shown instead is
 * the pair of timestamps that actually carries the information: when the last
 * photograph was TAKEN, by the device's clock, and when it ARRIVED. A job whose
 * last capture is Tuesday and whose last upload is Tuesday is in step; one
 * whose last capture is hours after its last upload has a device still holding
 * work, and that is a fact rather than a guess.
 *
 * The row links to the job's photographic record, which already renders every
 * photograph with its position, its integrity and — the one that matters — a
 * hand-set coordinate marked as hand-set. There is no second rendering of the
 * same evidence here, deliberately: two screens showing the same photograph
 * differently is how a product ends up with an honest page and a flattering
 * one.
 */
export function Installations(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<InstallationsResponse>(async () => {
    // ASK FOR THE SCOPE THE ROLE ACTUALLY HAS.
    //
    // The endpoint refuses an organisation-wide read to a role the matrix
    // scopes to its own work, which is correct — a board listing every
    // property the company is working at is not an installer's to see. But
    // this screen asked for the wide one unconditionally, so an installer
    // opening Installations got "Your role may not read job" on the one page
    // built for them. The permission was right and the request was wrong.
    //
    // The matrix is the single source of that decision, so it is asked rather
    // than a role name being matched here: a role added later with `own`
    // scope gets the right request without this file being touched.
    const me = await client.me();
    const mine = scopeFor(me.role, "job", "read") === "own";
    return client.installations({ mine });
  }, [client]);

  return (
    <>
      <div className="page-header spread">
        <h1>Installations</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <Async state={state} what="the installations board" reload={reload}>
        {(response) => <Board installations={response.installations} />}
      </Async>
    </>
  );
}

function Board({
  installations,
}: {
  readonly installations: readonly Installation[];
}): ReactNode {
  if (installations.length === 0) {
    return (
      <div className="state state--empty">
        <span className="state__glyph">
          <IconState kind="empty" />
        </span>
        <p className="state__title">Nothing in the field yet</p>
        <p className="state__body">
          A job appears here once an installer is assigned to it, or once the
          first photograph arrives from one. Assign an installer on the job.
        </p>
      </div>
    );
  }

  const photographs = installations.reduce((n, i) => n + i.photoCount, 0);

  return (
    <>
      <p className="lede" role="status" aria-live="polite">
        {describe(installations.length, photographs)}
      </p>

      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">
            Jobs with an installer assigned or photographs received. The
            reference is the property, as the installer entered it on the phone.
          </caption>
          <thead>
            <tr>
              <th scope="col">Reference</th>
              <th scope="col">Installer</th>
              <th scope="col">Stage</th>
              <th scope="col">Photographs</th>
              <th scope="col">Last taken</th>
              <th scope="col">Last received</th>
            </tr>
          </thead>
          <tbody>
            {installations.map((i) => (
              <tr key={i.jobId}>
                <th scope="row">
                  <a href={href.installation(i.jobId)}>{i.reference}</a>
                </th>
                <td>{i.installerName ?? <Unassigned />}</td>
                <td>{humanise(i.stage)}</td>
                <td>{i.photoCount}</td>
                <td>{i.lastCapturedAt ? utcMoment(i.lastCapturedAt) : "—"}</td>
                <td>{i.lastReceivedAt ? utcMoment(i.lastReceivedAt) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hint">
        Both times are shown because the difference between them is the only
        thing that tells you a phone is still holding work. Nothing here can
        know what has not arrived.
      </p>
    </>
  );
}

function Unassigned(): ReactNode {
  return <span className="muted">Nobody assigned</span>;
}

function describe(jobs: number, photographs: number): string {
  const j = `${jobs} ${jobs === 1 ? "installation" : "installations"}`;
  const p = `${photographs} ${photographs === 1 ? "photograph" : "photographs"}`;
  return `${j}, ${p} received.`;
}
